import type { Today } from "../../api.ts";
import { tokens, useTheme } from "../../glassTheme.tsx";
import { GlassCard } from "../glass/Glass.tsx";
import { TaskRow } from "./Row.tsx";
import { FlagCard } from "./FlagCard.tsx";
import { shortDate } from "../../theme.ts";

/**
 * The morning brief, composed by `get_today` (§1): heavy thing, one or two
 * dated items, the starving-value invitation, one flag, the question, the
 * rhythms footer. Inside a travel range the heavy thing, invitation and
 * question are absent and nothing says why.
 */
export function Morning({
  data, onOpen, onFlagsChanged,
}: { data: Today; onOpen: (id: string) => void; onFlagsChanged: () => void }) {
  const { colors } = useTheme();
  const traveling = !!data.travel;

  return (
    <div>
      {!traveling && data.heavy && (
        <GlassCard padding={16} style={{ cursor: "pointer" }} onClick={() => onOpen(data.heavy!.id)}>
          <div style={{
            fontFamily: tokens.font.sans, fontSize: tokens.type.caption.size,
            letterSpacing: tokens.type.caption.letterSpacing, textTransform: "uppercase",
            fontWeight: 600, color: colors.ink2,
          }}>{data.heavy.breadcrumb}</div>
          <div style={{
            fontFamily: tokens.font.serif, fontSize: tokens.type.heavyTitle.size,
            lineHeight: tokens.type.heavyTitle.lineHeight, letterSpacing: tokens.type.heavyTitle.letterSpacing,
            margin: "6px 0 8px", color: colors.ink,
          }}>{data.heavy.title}</div>
          {data.heavy.why_today && (
            <p style={{ margin: 0, fontSize: 15, color: colors.ink2 }}>{data.heavy.why_today}</p>
          )}
        </GlassCard>
      )}

      {data.then.length > 0 && (
        <GlassCard padding="2px 16px" style={{ marginTop: 16 }}>
          {data.then.map((t) => (
            <TaskRow key={t.id} task={t} onOpen={() => onOpen(t.id)} right={shortDate(t.due)} />
          ))}
        </GlassCard>
      )}

      {!traveling && data.quiet_hour && (
        <GlassCard padding={16} style={{ marginTop: 16, cursor: "pointer" }} onClick={() => onOpen(data.quiet_hour!.task.id)}>
          <div style={{
            fontFamily: tokens.font.sans, fontSize: tokens.type.caption.size,
            letterSpacing: tokens.type.caption.letterSpacing, textTransform: "uppercase",
            fontWeight: 600, color: colors.ink2,
          }}>{data.quiet_hour.value} · nothing is pushing this</div>
          <div style={{ marginTop: 4, fontSize: 15, color: colors.ink }}>{data.quiet_hour.task.title}</div>
        </GlassCard>
      )}

      {data.flag && <FlagCard flag={data.flag} today={data.date} onResolved={onFlagsChanged} />}

      {!traveling && data.question && (
        <div style={{ padding: "16px 4px 0" }}>
          <div style={{
            fontFamily: tokens.font.serif, fontStyle: "italic", fontSize: tokens.type.question.size,
            lineHeight: tokens.type.question.lineHeight, color: colors.ink,
          }}>{data.question}</div>
        </div>
      )}

      <div style={{ padding: "12px 4px 0", fontSize: 12.5, color: colors.ink2, textAlign: "center" }}>
        {data.rhythms.join(" · ")}
      </div>
    </div>
  );
}
